import React from 'react'
import { Brain, Bot, Server, Database, Cloud, Code, Shield, Cpu, TestTube, Users } from 'lucide-react'

const Expertise = () => {
  const expertiseAreas = [
    {
      icon: Bot,
      title: 'Agentic AI',
      skills: ['CrewAI', 'Anthropic Claude (Sonnet 4)', 'Multi-Agent Pipelines', 'Context Chaining', 'Pydantic Contracts', 'Human-in-the-Loop', 'Skyfire AI Payments', 'Composio'],
    },
    {
      icon: Brain,
      title: 'ML/AI Algorithms',
      skills: ['Logistic Regression', 'SVM', 'Decision Trees', 'Random Forests', 'Gradient Boosting', 'K-Means', 'GMM', 'DBSCAN', 'TF-IDF', 'SVD', 'NMF', 'CNN', 'RNN', 'LSTM', 'Transformers'],
    },
    {
      icon: Cpu,
      title: 'ML Frameworks & MLOps',
      skills: ['TensorFlow', 'PyTorch', 'Scikit-learn', 'Keras', 'XGBoost', 'LightGBM', 'Snowflake Cortex ML', 'MLflow', 'A/B Testing', 'Drift Detection', 'Feature Stores'],
    },
    {
      icon: Server,
      title: 'Fintech / Backend',
      skills: ['Spring Boot 2.x/3.x', 'Java 11/17', 'Spring Security', 'Spring Data JPA', 'Hibernate', 'Flyway', 'Firebase Auth', 'Stripe', 'OFAC Screening', 'KYC Workflows'],
    },
    {
      icon: Cloud,
      title: 'Cloud & Infrastructure',
      skills: ['AWS (SageMaker, Lambda, EC2, S3)', 'Oracle Cloud', 'TiDB Cloud Serverless', 'Docker', 'Kubernetes', 'Caddy', 'IaC', 'DevSecOps'],
    },
    {
      icon: Database,
      title: 'Data & Storage',
      skills: ['MySQL/TiDB', 'Snowflake', 'PostgreSQL', 'SQL', 'NoSQL', 'Vector DBs', 'Apache Kafka', 'Spark', 'ETL Pipelines', 'Data Warehousing'],
    },
    {
      icon: Code,
      title: 'Frontend / Full-Stack',
      skills: ['React 18', 'TypeScript', 'Vite', 'Streamlit', 'Plotly', 'React Native', 'GraphQL', 'RESTful APIs', 'Microservices'],
    },
    {
      icon: TestTube,
      title: 'Testing & DevOps',
      skills: ['Postman/Newman', 'CI/CD', 'Kubernetes', 'Git', 'Maven', 'Docker', '70+ endpoints', '19 functional groups'],
    },
    {
      icon: Users,
      title: 'Engineering Management',
      skills: ['AI-Centric SDLC', 'Team Building (30+ engineers)', 'Agile/Scrum', 'OKRs', 'Workforce Strategy', 'Security Standards', 'Model Governance'],
    },
  ]
  
  const languages = [
    { name: 'Python 3.11+', level: 95 },
    { name: 'Java 11/17', level: 90 },
    { name: 'JavaScript/TypeScript', level: 85 },
    { name: 'SQL', level: 90 },
  ]
  
  return (
    <section id="expertise" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Technical <span className="text-gradient">Expertise</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Comprehensive skill set built over 19+ years of hands-on engineering and leadership.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {expertiseAreas.map((area, index) => (
            <div key={index} className="glass rounded-2xl p-6 hover:border-primary/50 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:from-primary/30 group-hover:to-secondary/30 transition-all">
                  <area.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display font-semibold text-white">{area.title}</h3>
              </div>
              
              <div className="flex flex-wrap gap-1.5">
                {area.skills.map((skill, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        {/* Languages Proficiency */}
        <div className="glass rounded-2xl p-8">
          <h3 className="text-xl font-display font-semibold text-white mb-6 text-center">
            Programming Languages
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {languages.map((lang, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white font-medium">{lang.name}</span>
                  <span className="text-primary">{lang.level}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-1000"
                    style={{ width: `${lang.level}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Expertise
